import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { motion } from 'framer-motion';
import { Edit, Plus, Trash2, Dumbbell } from 'lucide-react';

export const ExerciseEditor = ({ workout, addExercise, updateExercise, removeExercise }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-card border border-border p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl text-white uppercase tracking-wider">
          <Edit className="inline h-5 w-5 mr-2 text-primary" />
          Exercises ({workout.exercises?.length || 0})
        </h2>
        <Button 
          onClick={addExercise}
          variant="outline"
          className="border-primary text-primary hover:bg-primary/10"
          data-testid="add-exercise-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Exercise
        </Button>
      </div>
      
      <div className="space-y-4">
        {workout.exercises?.map((exercise, index) => (
          <ExerciseCard
            key={index}
            exercise={exercise}
            index={index}
            updateExercise={updateExercise}
            removeExercise={removeExercise}
          />
        ))}
        
        {(!workout.exercises || workout.exercises.length === 0) && (
          <div className="text-center py-8 text-muted-foreground">
            <Dumbbell className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No exercises added yet</p>
            <p className="text-sm">Click "Add Exercise" to get started</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const ExerciseCard = ({ exercise, index, updateExercise, removeExercise }) => {
  const trackWeight = exercise.track_weight !== false; // default true
  return (
    <div className="bg-black border border-border rounded-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-primary font-heading">Exercise {index + 1}</span>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => removeExercise(index)}
          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="grid md:grid-cols-6 gap-4">
        <div className="md:col-span-2">
          <Label className="text-muted-foreground text-xs uppercase">Name</Label>
          <Input 
            value={exercise.name}
            onChange={(e) => updateExercise(index, 'name', e.target.value)}
            className="mt-1 bg-card border-border text-white text-sm"
          />
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Sets</Label>
          <Input 
            type="number"
            value={exercise.sets}
            onChange={(e) => updateExercise(index, 'sets', parseInt(e.target.value) || 0)}
            className="mt-1 bg-card border-border text-white text-sm"
          />
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Reps</Label>
          <Input 
            value={exercise.reps}
            onChange={(e) => updateExercise(index, 'reps', e.target.value)}
            className="mt-1 bg-card border-border text-white text-sm"
            placeholder="10 or 30 sec"
          />
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Duration (sec)</Label>
          <Input 
            type="number"
            value={exercise.duration_seconds || ''}
            onChange={(e) => updateExercise(index, 'duration_seconds', parseInt(e.target.value) || null)}
            className="mt-1 bg-card border-border text-white text-sm"
            placeholder="Optional"
          />
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Rest (sec)</Label>
          <Input 
            type="number"
            value={exercise.rest_seconds || 30}
            onChange={(e) => updateExercise(index, 'rest_seconds', parseInt(e.target.value) || 30)}
            className="mt-1 bg-card border-border text-white text-sm"
          />
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Transition (sec)</Label>
          <Input 
            type="number"
            value={exercise.transition_seconds || 45}
            onChange={(e) => updateExercise(index, 'transition_seconds', parseInt(e.target.value) || 45)}
            className="mt-1 bg-card border-border text-white text-sm"
          />
        </div>
      </div>

      {/* Weight-tracker admin controls */}
      <div className="mt-4 pt-4 border-t border-border/60 grid md:grid-cols-6 gap-4 items-end">
        <div className="md:col-span-2 flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={trackWeight}
              onChange={(e) => updateExercise(index, 'track_weight', e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
              data-testid={`track-weight-toggle-${index}`}
            />
            <span className="text-white text-sm">Track weight</span>
          </label>
          <span className="text-xs text-muted-foreground">
            {trackWeight ? 'Shows set-by-set weight logger' : 'Bodyweight / cardio — no logger'}
          </span>
        </div>
        <div className="md:col-span-2">
          <Label className="text-muted-foreground text-xs uppercase">Target Weight (lb)</Label>
          <Input
            type="number"
            inputMode="decimal"
            value={exercise.target_weight_lbs ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              updateExercise(index, 'target_weight_lbs', val === '' ? null : parseFloat(val));
            }}
            disabled={!trackWeight}
            className="mt-1 bg-card border-border text-white text-sm disabled:opacity-50"
            placeholder="Optional starting load"
            data-testid={`target-weight-${index}`}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Pre-fills the user's Set 1 weight the first time they see this lift.
          </p>
        </div>
      </div>
    </div>
  );
};
